/**
 * SearchBar - Search input component for header
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';

export const SearchBar = ({ isOpen, onToggle }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isXlScreen, setIsXlScreen] = useState(window.innerWidth >= 1280);
  const navigate = useNavigate();
  const searchRef = useRef(null);

  // Track screen size for responsive display (protected from browser extensions)
  useEffect(() => {
    const handleResize = () => setIsXlScreen(window.innerWidth >= 1280);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Note: Click outside handling is done in SearchDropdownPanel component

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      onToggle(false);
      setSearchQuery('');
    }
  };

  if (isOpen) {
    return (
      <div ref={searchRef}>
        {/* Extra large screens only: inline search (1280px+) */}
        {isXlScreen ? (
          <form onSubmit={handleSearch} style={{ display: 'flex', alignItems: 'center' }}>
            <Input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-48 h-8 input-editorial text-sm !ring-0 !ring-offset-0 focus:!ring-0 focus-visible:!ring-0 border-black/20 focus:border-black/40"
              autoFocus
              data-testid="search-input"
            />
          </form>
        ) : (
          /* Below 1280px: clicking the icon closes the search */
          <button
            type="button"
            onClick={() => onToggle(false)}
            className="p-2 text-site-text-muted hover:text-site-text transition-colors"
            data-testid="search-icon-active"
          >
            <Search className="w-5 h-5" />
          </button>
        )}
      </div>
    );
  }

  return (
    <button
      onClick={() => onToggle(true)}
      className="p-2 hover:bg-site-bg-dark hover:text-white transition-colors"
      data-testid="open-search-btn"
    >
      <Search className="w-5 h-5" />
    </button>
  );
};

// Separate component for the dropdown search panel (rendered in Header)
// This pushes content down instead of overlaying
export const SearchDropdownPanel = ({ isOpen, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const panelRef = useRef(null);

  // Close on click outside - uses click event so links can navigate first
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      // Check if clicked element is inside the form
      const form = panelRef.current?.querySelector('form');
      if (form && form.contains(event.target)) {
        return; // Don't close if clicking inside the form
      }
      
      // Close the search for any other click (navigation will have already started)
      onClose();
    };

    // Use click event (not mousedown) so links navigate before we close
    const timer = setTimeout(() => {
      document.addEventListener('click', handleClickOutside, false);
    }, 150);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleClickOutside, false);
    };
  }, [isOpen, onClose]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      onClose();
      setSearchQuery('');
    }
  };

  // Handle click inside panel to prevent closing
  const handlePanelClick = (e) => {
    // Only stop propagation if clicking on the form elements, not on padding/empty space
    // This allows clicking on the empty space below the input to close the search
  };

  if (!isOpen) return null;

  return (
    <div 
      ref={panelRef}
      className="xl:hidden bg-site-bg px-4 pt-1 pb-3"
    >
      <form 
        onSubmit={handleSearch} 
        className="flex items-center gap-2"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <Input
          type="text"
          placeholder="Search products..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onMouseDown={(e) => e.stopPropagation()}
          className="flex-1 h-10 input-editorial text-sm !ring-0 !ring-offset-0 focus:!ring-0 focus-visible:!ring-0 !shadow-none !border-black/20 focus:!border-black/30"
          autoFocus
          data-testid="search-input-mobile"
        />
        <button
          type="submit"
          className="px-4 h-10 bg-site-bg-dark text-white font-mono text-xs uppercase tracking-widest hover:bg-black transition-colors"
          onMouseDown={(e) => e.stopPropagation()}
        >
          Search
        </button>
      </form>
    </div>
  );
};

export default SearchBar;
